import React from 'react';
import styled from 'styled-components';

import { PhoneWrapper } from 'src/svg/phone-wrapper';
import { CustomNavigationItemType, GqlCustomNavigationItem } from 'types/graphql';

const MAX_PREVIEW_ITEMS = 6;

type CustomNavigationPreviewProps = {
  items: GqlCustomNavigationItem[];
  isNested?: boolean;
  onNavigateUp?: () => void;
  onSectionClick?: (sectionId: string) => void;
  title?: string;
};

export function CustomNavigationPreview({
  items,
  isNested = false,
  onNavigateUp,
  onSectionClick,
  title = 'Menu',
}: CustomNavigationPreviewProps): JSX.Element {
  const displayItems = items.slice(0, MAX_PREVIEW_ITEMS);
  const isBackButtonClickable = isNested && !!onNavigateUp;

  return (
    <Container>
      <PhoneContainer>
        <PhoneWrapper />
        <ContentContainer>
          <TabBar>
            <CloseButton
              as={isBackButtonClickable ? 'button' : undefined}
              type={isBackButtonClickable ? 'button' : undefined}
              onClick={isBackButtonClickable ? onNavigateUp : undefined}
            >
              {isBackButtonClickable ? '‹' : '×'}
            </CloseButton>
            <Tab $isActive>{title}</Tab>
            <Tab>Categories</Tab>
            <Tab>Brands</Tab>
            <Tab>Store Info</Tab>
          </TabBar>

          <MenuList>
            {displayItems.map((item) => {
              const isSection = item.navigationType === CustomNavigationItemType.section;
              const isClickableSection = isSection && !!onSectionClick;

              return (
                <MenuItem
                  key={item.id}
                  as={isClickableSection ? 'button' : undefined}
                  type={isClickableSection ? 'button' : undefined}
                  onClick={isClickableSection ? () => onSectionClick(item.id) : undefined}
                  $isClickable={isClickableSection}
                >
                  <MenuItemLabel>{item.label}</MenuItemLabel>
                  {isSection && <ChevronIcon>›</ChevronIcon>}
                </MenuItem>
              );
            })}
          </MenuList>
        </ContentContainer>
      </PhoneContainer>
    </Container>
  );
}

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const PhoneContainer = styled.div`
  border-radius: 12px;
  overflow: hidden;
  padding: 20px;
  position: relative;
`;

const ContentContainer = styled.div`
  left: 31px;
  overflow: hidden;
  position: absolute;
  top: 60px;
  width: calc(100% - 62px);
`;

const TabBar = styled.div`
  align-items: center;
  display: flex;
`;

const CloseButton = styled.span`
  border-bottom: 1px solid #e8eaed;
  color: #000;
  cursor: pointer;
  flex: 0 1 20px;
  font-size: 20px;
  font-weight: 300;
  line-height: 1;
  min-width: 26px;
  padding: 8px 8px 6px 9px;
  text-align: center;

  &[type='button'] {
    appearance: none;
    background: none;
    border-left: none;
    border-right: none;
    border-top: none;
    font: inherit;
    margin: 0;
    width: 100%;
  }
`;

const Tab = styled.span<{ $isActive?: boolean }>`
  border-bottom: 1px solid ${({ $isActive }) => ($isActive ? '#0b99e6' : '#e8eaed')};
  color: ${({ $isActive }) => ($isActive ? '#000' : '#677882')};
  cursor: pointer;
  flex: 1 0 0px;
  font-size: 12px;
  font-weight: ${({ $isActive }) => ($isActive ? '600' : '400')};
  overflow: clip;
  padding: 8px 12px;
  position: relative;
  white-space: nowrap;
`;

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
`;

const MenuItem = styled.div<{ $isClickable?: boolean }>`
  align-items: center;
  border-bottom: 1px solid #f0f2f4;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  display: flex;
  justify-content: space-between;
  padding: 16px 20px;
  text-align: left;
  width: 100%;

  &[type='button'] {
    appearance: none;
    background: none;
    border-top: 0;
    border-left: 0;
    border-right: 0;
    font: inherit;
  }
`;

const MenuItemLabel = styled.span`
  color: #000;
  font-size: 16px;
`;

const ChevronIcon = styled.span`
  color: #677882;
  font-size: 20px;
`;
